<template>
  <v-container fluid class="wfm-page">
    <v-card>
      <v-card-title>WFM</v-card-title>
      <v-card-text>
        <v-form ref="form" lazy-validation>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-menu
                v-model="periodMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedPeriod"
                    label="Период"
                    prepend-icon="mdi-calendar"
                    readonly
                    :rules="[rules.requiredPeriod]"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.period"
                  range
                  locale="Ru-ru"
                  @input="onPeriodInput"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <ProjectSelector v-model="filters.projectIds" />
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <GroupSelector
                v-model="filters.groupIds"
                no-rules
                @change:users="fillUsers"
              />
            </v-col>

            <v-col cols="12" sm="6" md="1">
              <v-text-field
                v-model.number="filters.sla"
                type="number"
                label="SLA"
                suffix="%"
                min="1"
                max="100"
              />
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filters.plan"
                :items="plans"
                :loading="plansLoading"
                item-value="id"
                item-text="title"
                label="План"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="d-flex justify-end">
              <v-btn
                color="success"
                :loading="loading"
                :disabled="loading"
                @click="loadReport"
              >
                Рассчитать
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <div v-if="report" class="wfm-report mt-5">
      <div class="wfm-head">
        <div>
          <h1 class="wfm-title">{{ reportTitle }}</h1>
          <div class="wfm-sub">
            План {{ lastPayload && lastPayload.plan }} · цель {{ slaPair }}
          </div>
        </div>
        <div class="wfm-head-actions">
          <v-btn
            color="success"
            :loading="excelLoading"
            :disabled="excelLoading || !lastPayload"
            @click="downloadExcel"
          >
            <v-icon left>mdi-microsoft-excel</v-icon>
            Скачать Excel
          </v-btn>
          <span class="wfm-pill">{{ slaPair }} по каждой линии</span>
        </div>
      </div>

      <div class="wfm-kpis">
        <v-tooltip
          v-for="item in kpiItems"
          :key="item.key"
          bottom
          max-width="280"
        >
          <template #activator="{ on, attrs }">
            <div class="wfm-kpi" v-bind="attrs" v-on="on">
              <div class="wfm-kpi-value" :class="{ 'is-alert': item.key === 'loss_pct' }">
                {{ formatKpiValue(item.value) }}
              </div>
              <div class="wfm-kpi-label">{{ item.label }}</div>
            </div>
          </template>
          <span>{{ item.description || item.label }}</span>
        </v-tooltip>
      </div>

      <div class="wfm-note">
        Дыры считаются <strong>по факту</strong> (по головам, а не по среднему). Разовые аварийные пики (ОТН) и наплыв 1-го не закладываем.
      </div>

      <div class="wfm-summary">
        <strong>Главное (по факту).</strong>
        {{ summaryText }}
      </div>

      <section class="wfm-block">
        <h2 class="wfm-section-title">Нагрузка по дням</h2>
        <table class="wfm-load-table">
          <thead>
            <tr>
              <th class="col-day">День</th>
              <th class="col-num">Звонков</th>
              <th class="col-num">Присут.</th>
              <th class="col-num">Ёмкость, мин</th>
              <th class="col-occ">Спрос ÷ ёмкость</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in loadDays" :key="row.day">
              <td class="col-day">{{ row.day }}</td>
              <td class="col-num">{{ formatNumber(row.calls) }}</td>
              <td class="col-num">{{ formatNumber(row.present) }}</td>
              <td class="col-num">{{ formatNumber(row.capacity) }}</td>
              <td class="col-occ">
                <div class="wfm-occ">
                  <span class="wfm-occ-pct">{{ row.occupancy }}%</span>
                  <div class="wfm-occ-bar">
                    <div class="wfm-occ-fill" :style="{ width: occupancyWidth(row.occupancy) }" />
                  </div>
                </div>
              </td>
            </tr>
            <tr v-if="!loadDays.length">
              <td colspan="5" class="wfm-empty">Нет данных</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="wfm-block">
        <h2 class="wfm-section-title">Обучение операторов ({{ trainingRows.length }})</h2>
        <v-simple-table dense class="wfm-table">
          <thead>
            <tr>
              <th>Оператор</th>
              <th>Режим</th>
              <th>Смена</th>
              <th class="text-right">Загр.</th>
              <th>Обучить линиям</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in trainingRows" :key="'tr-' + idx">
              <td>{{ row.name }}</td>
              <td>{{ row.reg }}</td>
              <td>{{ row.shift }}</td>
              <td class="text-right">{{ row.load }}</td>
              <td>{{ row.lines }}</td>
            </tr>
            <tr v-if="!trainingRows.length">
              <td colspan="5" class="grey--text">Дыр нет — обучение не требуется</td>
            </tr>
          </tbody>
        </v-simple-table>
      </section>

      <section class="wfm-block">
        <h2 class="wfm-section-title">Изменение времени смен</h2>
        <div class="wfm-section-hint">Начать раньше / позже</div>
        <v-simple-table dense class="wfm-table">
          <thead>
            <tr>
              <th>Оператор</th>
              <th>Режим</th>
              <th>Было</th>
              <th>Стало</th>
              <th>Сдвиг</th>
              <th>Зачем (причина)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in shiftRows" :key="'sh-' + idx">
              <td>{{ row.name }}</td>
              <td>{{ row.reg }}</td>
              <td>{{ row.was }}</td>
              <td>{{ row.became }}</td>
              <td>{{ row.shift }}</td>
              <td>{{ row.reason }}</td>
            </tr>
            <tr v-if="!shiftRows.length">
              <td colspan="6" class="grey--text">Время смен не меняем — сдвиги без реальной причины убраны.</td>
            </tr>
          </tbody>
        </v-simple-table>
      </section>

      <div v-if="releasedNames.length" class="wfm-released">
        <strong>{{ releasedTitle }} ({{ releasedNames.length }}):</strong>
        {{ releasedNames.join(', ') }}
      </div>

      <section class="wfm-block">
        <h2 class="wfm-section-title">Расписание</h2>
        <div class="wfm-table-scroll">
          <v-simple-table dense class="wfm-table wfm-schedule">
            <thead>
              <tr>
                <th class="wfm-sticky">Оператор</th>
                <th class="text-center">Нав.</th>
                <th
                  v-for="day in scheduleDays"
                  :key="'d-' + day"
                  class="text-center"
                >
                  {{ day }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in scheduleRows" :key="'sch-' + idx">
                <td class="wfm-sticky">{{ row.name }}</td>
                <td class="text-center">{{ row.nsk }}</td>
                <td
                  v-for="(cell, dayIdx) in row.days"
                  :key="'c-' + idx + '-' + dayIdx"
                  class="text-center"
                  :class="{ 'is-off': cell === 'В' }"
                >
                  {{ cell }}
                </td>
              </tr>
              <tr v-if="!scheduleRows.length">
                <td :colspan="2 + scheduleDays.length" class="text-center grey--text">Нет данных</td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
      </section>

      <section v-if="actionRows.length" class="wfm-block">
        <h2 class="wfm-section-title">Меры</h2>
        <v-simple-table dense class="wfm-table">
          <thead>
            <tr>
              <th>Линия</th>
              <th>Мера</th>
              <th>Причина</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in actionRows" :key="'act-' + idx">
              <td>{{ row.project }}</td>
              <td>{{ row.label || row.measure }}</td>
              <td>{{ row.reason }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </section>

      <section v-if="shortageRows.length" class="wfm-block">
        <h2 class="wfm-section-title">Нехватка</h2>
        <v-simple-table dense class="wfm-table">
          <thead>
            <tr>
              <th>Окно</th>
              <th class="text-right">Нужно</th>
              <th>Навыки</th>
              <th>Проекты</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in shortageRows" :key="'shrt-' + idx">
              <td>{{ row.window }}</td>
              <td class="text-right">{{ row.count }}</td>
              <td>{{ formatList(row.skills) }}</td>
              <td>{{ formatList(row.projects) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </section>

      <section v-if="surplusRows.length" class="wfm-block">
        <h2 class="wfm-section-title">Лишние</h2>
        <v-simple-table dense class="wfm-table">
          <thead>
            <tr>
              <th>Оператор</th>
              <th>Навыки</th>
              <th class="text-right">Загрузка</th>
              <th>Почему</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in surplusRows" :key="'sur-' + idx">
              <td>{{ row.name }}</td>
              <td>{{ formatList(row.skills) }}</td>
              <td class="text-right">{{ row.load }}</td>
              <td>{{ row.reason }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </section>
    </div>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import ProjectSelector from '~/components/partials/selectors/ProjectSelector.vue'
import GroupSelector from '~/components/partials/selectors/GroupSelector.vue'

const FALLBACK_PLANS = [
  { id: 1, title: 'Баланс (все в строю)' },
  { id: 2, title: 'Экономия (высвободить лишних)' },
]

export default {
  name: 'WfmReport',

  components: {
    ProjectSelector,
    GroupSelector,
  },

  data() {
    return {
      loading: false,
      excelLoading: false,
      plansLoading: false,
      periodMenu: false,
      plans: FALLBACK_PLANS,
      filters: {
        period: [
          moment().subtract(6, 'days').format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD'),
        ],
        projectIds: [],
        groupIds: [],
        userIds: [],
        sla: 80,
        plan: 1,
      },
      report: null,
      lastPayload: null,
      rules: {
        requiredPeriod: () => (this.sortedPeriod.length === 2) || 'Укажите период',
      },
    }
  },

  computed: {
    sortedPeriod() {
      const period = this.filters.period
      if (!Array.isArray(period) || period.filter(Boolean).length < 2) return []
      return [...period].sort()
    },

    formattedPeriod() {
      if (this.sortedPeriod.length !== 2) return ''
      return `${moment(this.sortedPeriod[0]).format('DD.MM.YYYY')} — ${moment(this.sortedPeriod[1]).format('DD.MM.YYYY')}`
    },

    slaValue() {
      const sla = (this.lastPayload && this.lastPayload.sla) || this.filters.sla || 80
      return Number(sla) || 80
    },

    slaPair() {
      return `${this.slaValue}/${100 - this.slaValue}`
    },

    reportTitle() {
      const period = (this.lastPayload && this.lastPayload.period) || this.sortedPeriod
      if (!period || period.length < 2) return 'Операторы'
      const from = moment(period[0])
      const days = moment(period[1]).diff(from, 'days') + 1
      return `Операторы — план на ${days} дн. с ${from.format('DD.MM.YYYY')}`
    },

    kpiItems() {
      return (this.report && Array.isArray(this.report.kpi)) ? this.report.kpi : []
    },

    insights() {
      return (this.report && this.report.insights) || {}
    },

    loadDays() {
      return (this.report && Array.isArray(this.report.load_days)) ? this.report.load_days : []
    },

    trainingRows() {
      const raw = (this.report && this.report.training) || []
      return raw.map((row) => ({
        name: row.name || row.operator || '—',
        reg: row.reg || row.mode || '—',
        shift: row.shift || '—',
        load: row.load == null || row.load === '' ? '—' : row.load,
        lines: this.formatList(row.lines),
      }))
    },

    shiftRows() {
      const raw = (this.report && this.report.shift_changes) || []
      return raw.map((row) => ({
        name: row.name || row.operator || '—',
        reg: row.reg || row.mode || '—',
        was: row.was || row.from || '—',
        became: row.became || row.to || '—',
        shift: row.shift || row.delta || '—',
        reason: row.reason || row.why || '—',
      }))
    },

    releasedNames() {
      const raw = (this.report && this.report.released) || []
      return raw
        .map((row) => (typeof row === 'string' ? row : row.name))
        .filter(Boolean)
    },

    releasedTitle() {
      const plan = this.lastPayload && this.lastPayload.plan
      return plan === 2 ? 'Высвобождение' : 'Новые в графике'
    },

    scheduleDays() {
      if (this.loadDays.length) return this.loadDays.map((row) => row.day)
      return this.sortedPeriod
    },

    scheduleRows() {
      return (this.report && Array.isArray(this.report.schedule)) ? this.report.schedule : []
    },

    actionRows() {
      return (this.report && Array.isArray(this.report.actions)) ? this.report.actions : []
    },

    shortageRows() {
      return (this.report && Array.isArray(this.report.shortage)) ? this.report.shortage : []
    },

    surplusRows() {
      return (this.report && Array.isArray(this.report.surplus)) ? this.report.surplus : []
    },

    summaryText() {
      const accepted = this.kpiValue('accepted_day')
      const offered = this.kpiValue('offered_day')
      const loss = this.kpiValue('loss_pct')
      const gaps = this.insights.honest_gaps != null ? this.insights.honest_gaps : 0
      const peak = this.insights.peak_deficit != null ? this.insights.peak_deficit : 0

      const parts = []
      if (accepted != null && offered != null) {
        parts.push(`Принимается ~${this.formatPlain(accepted)} из ~${this.formatPlain(offered)} звонков/день`)
      }
      if (loss != null) {
        parts.push(`потери ~ ${loss}`)
      }

      let text = parts.join(', ')
      if (text) text += '.'

      return `${text} Честных часо-дыр — ${gaps}, пик дефицита — ${peak}. Приоритеты обучения и расписание — ниже.`.replace(/^\s+/, '')
    },
  },

  mounted() {
    this.loadPlans()
  },

  methods: {
    ...mapActions({
      fetchPlans: 'wfm/fetchPlans',
      fetchReport: 'wfm/fetchReport',
      exportReport: 'wfm/exportReport',
      setError: 'shared/setError',
      setLoading: 'shared/setLoading',
    }),

    async loadPlans() {
      this.plansLoading = true
      try {
        const plans = await this.fetchPlans()
        if (Array.isArray(plans) && plans.length) this.plans = plans
      } catch (error) {
        this.plans = FALLBACK_PLANS
      } finally {
        this.plansLoading = false
      }
    },

    fillUsers(ids) {
      this.filters.userIds = Array.isArray(ids) ? ids : []
    },

    onPeriodInput(val) {
      if (Array.isArray(val) && val.length === 2) this.periodMenu = false
    },

    buildPayload() {
      const period = this.sortedPeriod
      const payload = {
        period,
        project_ids: this.filters.projectIds,
        user_ids: this.filters.userIds || [],
        plan: this.filters.plan || 1,
      }
      if (this.filters.sla) payload.sla = Number(this.filters.sla)
      return payload
    },

    async loadReport() {
      if (!this.$refs.form.validate()) return

      this.loading = true
      this.setLoading(true)
      try {
        const payload = this.buildPayload()
        this.report = await this.fetchReport(payload)
        this.lastPayload = payload
      } catch (error) {
        this.report = null
        this.lastPayload = null
        const payload = (error.response && error.response.data) || { message: error }
        this.setError(payload)
      } finally {
        this.loading = false
        this.setLoading(false)
      }
    },

    async downloadExcel() {
      if (!this.lastPayload) return
      this.excelLoading = true
      try {
        const resp = await this.exportReport(this.lastPayload)
        this.downloadBlob(resp)
      } catch (error) {
        const payload = (error.response && error.response.data) || { message: error }
        this.setError(payload)
      } finally {
        this.excelLoading = false
      }
    },

    downloadBlob(response) {
      try {
        this.utils.downloadXlsx(response)
      } catch (e) {
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        const from = (this.lastPayload && this.lastPayload.period && this.lastPayload.period[0]) || ''
        const plan = (this.lastPayload && this.lastPayload.plan) || 1
        link.href = url
        link.download = `wfm_${from}_plan${plan}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      }
    },

    kpiValue(key) {
      const item = this.kpiItems.find((row) => row.key === key)
      return item ? item.value : null
    },

    formatKpiValue(value) {
      if (value == null || value === '') return '—'
      if (typeof value === 'number') return value.toLocaleString('ru-RU')
      return value
    },

    formatPlain(value) {
      if (value == null || value === '') return '—'
      return String(value)
    },

    formatNumber(value) {
      if (value == null || value === '') return '—'
      return Number(value).toLocaleString('ru-RU')
    },

    formatList(value) {
      if (Array.isArray(value)) return value.filter(Boolean).join(', ')
      return value || '—'
    },

    occupancyWidth(value) {
      const pct = Math.max(0, Math.min(100, Number(value) || 0))
      return `${pct}%`
    },
  },
}
</script>

<style scoped>
.wfm-page {
  padding-bottom: 32px;
}

.wfm-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.wfm-title {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 4px;
  color: rgba(0, 0, 0, 0.87);
}

.wfm-sub {
  color: #6b7280;
  font-size: 14px;
}

.wfm-head-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.wfm-pill {
  display: inline-flex;
  align-items: center;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.wfm-kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}

.wfm-kpi {
  background: #fff;
  border: 1px solid #e6e8eb;
  border-radius: 8px;
  padding: 16px 18px;
  color: rgba(0, 0, 0, 0.87);
}

.wfm-kpi-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
}

.wfm-kpi-value.is-alert {
  color: #c62828;
}

.wfm-kpi-label {
  margin-top: 6px;
  color: #6b7280;
  font-size: 13px;
}

.wfm-note {
  margin: 14px 0 16px;
  color: #6b7280;
  font-size: 13px;
}

.wfm-summary {
  background: #f3f4f6;
  border-left: 4px solid #c62828;
  border-radius: 4px;
  padding: 14px 16px;
  margin-bottom: 24px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.87);
}

.wfm-block {
  background: #fff;
  border: 1px solid #e6e8eb;
  border-radius: 8px;
  padding: 16px 18px 8px;
  margin-bottom: 16px;
  color: rgba(0, 0, 0, 0.87);
}

.wfm-section-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin: 0 0 10px;
}

.wfm-section-hint {
  color: #6b7280;
  font-size: 13px;
  margin: -4px 0 10px;
}

.wfm-table {
  background: transparent;
}

.wfm-table th {
  color: #6b7280 !important;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.wfm-load-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.wfm-load-table th,
.wfm-load-table td {
  padding: 10px 20px;
  border-bottom: 1px solid #eef0f2;
  vertical-align: middle;
  font-size: 14px;
}

.wfm-load-table th {
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  padding-bottom: 8px;
}

.wfm-load-table tbody tr:last-child td {
  border-bottom: 0;
}

.wfm-load-table .col-day {
  width: 15%;
  text-align: left;
  white-space: nowrap;
}

.wfm-load-table .col-num {
  width: 15%;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.wfm-load-table .col-occ {
  width: 40%;
  text-align: left;
  /* как зазор между двумя правыми числовыми колонками (~ширина одного столбца) */
  padding-left: 15%;
}

.wfm-load-table th.col-occ {
  text-align: left;
}

.wfm-load-table .wfm-empty {
  text-align: center;
  color: #9ca3af;
}

.wfm-occ {
  display: flex;
  align-items: center;
  width: 100%;
}

.wfm-occ-pct {
  width: 3em;
  flex-shrink: 0;
  text-align: left;
  font-variant-numeric: tabular-nums;
}

.wfm-occ-bar {
  width: 180px;
  flex: none;
  margin-left: auto;
  height: 10px;
  background: #e8eaed;
  border-radius: 999px;
  overflow: hidden;
}

.wfm-occ-fill {
  height: 100%;
  background: #2e7d32;
  border-radius: 999px;
}

.wfm-released {
  margin: 8px 0 16px;
  line-height: 1.5;
}

.wfm-table-scroll {
  overflow-x: auto;
}

.wfm-schedule td.is-off {
  color: #9ca3af;
}

.wfm-sticky {
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 1;
  white-space: nowrap;
}

/* Dark theme */
.theme--dark .wfm-title,
.theme--dark .wfm-released {
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .wfm-sub,
.theme--dark .wfm-note,
.theme--dark .wfm-section-title,
.theme--dark .wfm-section-hint,
.theme--dark .wfm-kpi-label,
.theme--dark .wfm-load-table th,
.theme--dark .wfm-table th {
  color: rgba(255, 255, 255, 0.6) !important;
}

.theme--dark .wfm-pill {
  background: rgba(46, 125, 50, 0.25);
  color: #81c784;
}

.theme--dark .wfm-kpi,
.theme--dark .wfm-block {
  background: #1e1e1e;
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .wfm-summary {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .wfm-kpi-value.is-alert {
  color: #ef5350;
}

.theme--dark .wfm-load-table th,
.theme--dark .wfm-load-table td {
  border-bottom-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .wfm-occ-bar {
  background: rgba(255, 255, 255, 0.12);
}

.theme--dark .wfm-sticky {
  background: #1e1e1e;
}

.theme--dark .wfm-schedule td.is-off,
.theme--dark .wfm-load-table .wfm-empty {
  color: rgba(255, 255, 255, 0.38);
}

@media (max-width: 1264px) {
  .wfm-kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .wfm-head {
    flex-direction: column;
  }

  .wfm-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
